import { useMutation } from '@platform/api-client';
import type { TypedClient } from '@platform/openapi';
import { useRef, useState } from 'react';
import type { Customer } from '../customers/customers.ts';
import { delegationNamed, governorateNamed } from '../geography/useGeoAreas.ts';
import { usePageContext } from '../usePageContext.ts';
import { CustomerPicker } from './CustomerPicker.tsx';
import { emptyOrderForm, orderBodyOf, type NewOrderFormValues } from './new-order-form.ts';
import { OrderForm, useOrderForm } from './OrderForm.tsx';
import type { OrderFormData } from './OrderFormFrame.tsx';
import { STOCK_RESOURCES } from './orders.ts';

export type NewOrderFormProps = OrderFormData & {
  api: TypedClient;
  onCreated: (orderId: string) => void;
};

export const NewOrderForm = ({
  api,
  currency,
  geography,
  depots,
  companies,
  onCreated,
}: NewOrderFormProps) => {
  const { t, notify, describeError, invalidate } = usePageContext();
  const idempotencyKey = useRef(crypto.randomUUID());
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerMissing, setCustomerMissing] = useState(false);
  const form = useOrderForm(emptyOrderForm((depots.find((depot) => depot.isDefault) ?? depots[0])?.id ?? ''));

  const pickCustomer = (picked: Customer | null) => {
    setCustomer(picked);
    setCustomerMissing(false);
    if (picked === null) return;
    const pickedGovernorate = governorateNamed(geography, picked.governorate);
    form.setValue('address', picked.address ?? '');
    form.setValue('governorate', pickedGovernorate?.code ?? '');
    form.setValue('delegation', delegationNamed(pickedGovernorate, picked.city) ?? '');
    form.setValue('postalCode', picked.postalCode ?? '');
  };

  const create = useMutation({
    mutationFn: (values: NewOrderFormValues) =>
      api.post('/admin/orders', {
        body: orderBodyOf(customer?.id ?? '', values),
        idempotencyKey: idempotencyKey.current,
      }),
    onSuccess: async (order) => {
      notify(t('newOrder.created', { number: order.number }));
      await invalidate(...STOCK_RESOURCES);
      onCreated(order.id);
    },
  });

  return (
    <OrderForm
      api={api}
      form={form}
      currency={currency}
      geography={geography}
      depots={depots}
      companies={companies}
      submitLabel={create.isPending ? t('newOrder.creating') : t('newOrder.create')}
      submitting={create.isPending}
      errorMessage={create.isError ? (describeError(create.error) ?? null) : null}
      customerChosen={customer !== null}
      customer={
        <CustomerPicker
          api={api}
          selected={customer}
          errorText={customerMissing ? t('newOrder.customerRequired') : undefined}
          onSelect={pickCustomer}
        />
      }
      onSubmit={(event) => {
        setCustomerMissing(customer === null);
        void form.handleSubmit((values) => {
          if (customer !== null) create.mutate(values);
        })(event);
      }}
    />
  );
};
